/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import utils from '../../../common/utils';
import {
  getCurrentPromptEditorMode,
  isCataloguePromptEditorMode,
  PROMPT_EDITOR_MODE_INLINE
} from './promptEditorModeUtils';

/**
 * Build agentConfig for graph create/update.
 * Catalogue mode + bound refs → refs only (backend binds/hydrates).
 * Inline mode → always persist prompt/userPrompt; leave existing refs on the document.
 *
 * @param {object} config
 * @param {string} [defaultSystemPrompt]
 * @param {'catalogue'|'inline'} [promptEditorMode] — defaults to current selected tenant mode
 */
export default function buildAgentConfigPayload(
  config,
  defaultSystemPrompt = '',
  promptEditorMode = getCurrentPromptEditorMode()
) {
  const promptRef = config?.promptRef;
  const promptVersionRef = config?.promptVersionRef;
  const hasPromptBinding = Boolean(promptRef && promptVersionRef);
  const useCatalogueBinding = isCataloguePromptEditorMode(promptEditorMode) && hasPromptBinding;
  const base = {
    model: config?.model?.model || 'gpt-5-mini',
    provider: config?.model?.provider || 'open-ai',
    maxTokens: config?.model?.maxTokens || 200,
    isInternal: config?.isInternal ?? false,
    additionalMetadata: config?.additionalMetadata || {},
    otherParameters: config?.otherParameters || {},
    providerParameters: config?.providerParameters || {}
  };
  const payload = { ...base };

  const systemPrompt = config?.systemPrompt ?? defaultSystemPrompt;
  const userPrompt = config?.userPrompt ?? '';
  const encodedPrompt = systemPrompt ? utils.encodedData(systemPrompt) : systemPrompt;
  const encodedUserPrompt = userPrompt ? utils.encodedData(userPrompt) : userPrompt;

  if (useCatalogueBinding) {
    payload.promptRef = promptRef;
    payload.promptVersionRef = promptVersionRef;
    // Also keep inline text so graphs remain editable if advanced PMS is later turned OFF.
    if (encodedPrompt != null && encodedPrompt !== '') {
      payload.prompt = encodedPrompt;
    }
    if (encodedUserPrompt != null && encodedUserPrompt !== '') {
      payload.userPrompt = encodedUserPrompt;
    }
    return payload;
  }

  payload.prompt = encodedPrompt;
  payload.userPrompt = encodedUserPrompt;

  // Inline mode: leave pilot refs on the document; do not treat them as source of truth.
  if (promptEditorMode === PROMPT_EDITOR_MODE_INLINE && hasPromptBinding) {
    payload.promptRef = promptRef;
    payload.promptVersionRef = promptVersionRef;
  }

  return payload;
}
